/**
 * Module #23 chunk 1 — urgency tiering for lot close countdowns.
 *
 * Maps "time remaining" to a visual urgency tier consumed by Countdown +
 * UrgencyBadge. Thresholds chosen to give bidders meaningful warning at each
 * coarsening of the countdown (days → hours → minutes → seconds).
 */

export type UrgencyTier = 'low' | 'medium' | 'high' | 'critical' | 'ended';

/** Cutoffs in ms, inclusive upper bound for each tier. */
const TIER_MS = {
  critical: 5 * 60 * 1000, // < 5 min remaining
  high: 60 * 60 * 1000, // < 1 hr remaining (and ≥ 5 min)
  medium: 24 * 60 * 60 * 1000, // < 24 hr remaining (and ≥ 1 hr)
  // anything ≥ 24 hr → 'low'
} as const;

export function urgencyTierFromMs(diffMs: number): UrgencyTier {
  if (diffMs <= 0) return 'ended';
  if (diffMs < TIER_MS.critical) return 'critical';
  if (diffMs < TIER_MS.high) return 'high';
  if (diffMs < TIER_MS.medium) return 'medium';
  return 'low';
}

export function urgencyTierFromIso(endsAt: string | null, nowMs: number): UrgencyTier | null {
  if (!endsAt) return null;
  const endMs = new Date(endsAt).getTime();
  if (Number.isNaN(endMs)) return null;
  return urgencyTierFromMs(endMs - nowMs);
}

/** Human-readable label for the urgency tier (used in accessibility text + chips). */
export function urgencyLabel(tier: UrgencyTier): string {
  switch (tier) {
    case 'low':
      return 'Days left';
    case 'medium':
      return 'Hours left';
    case 'high':
      return 'Closing soon';
    case 'critical':
      return 'Final minutes';
    case 'ended':
      return 'Ended';
  }
}
